/**
 * The app's API plumbing: the selected org, the one `ProDocClient` the app's
 * own screens share, and `api()` for anything org-scoped that is not one of
 * the four capabilities.
 *
 * `api()` is a thin layer over `session.authed` from `@fiveox/shell` — that is
 * where the bearer, the single serialized refresh and the one place a session
 * ends already live (see `session.ts`). What this file adds is the two things
 * the shell cannot know about: **the org header** and **this app's error
 * envelope**. `createProDocClient` adds the same two for a host that is not
 * this app (ADR-0010), so the error class is its and re-exported here.
 */

import { ApiError, createProDocClient } from '@fiveox/prodoc-ui'

import { SERVICE_API } from './config'
import { SELECTED_ORG_KEY, setSelectedOrgId } from './orgs'
import { createParsingApi } from './parsing'
import { session } from './session'

export { ApiError }

// -- the selected org --------------------------------------------------------
//
// A user belongs to many orgs and switches between them, so the selected org
// is **not** derivable from the token: the client sends it and the service
// proves the user may act in it (FiveOX ADR-0029).

let currentOrgId: string | null =
  typeof localStorage === 'undefined' ? null : localStorage.getItem(SELECTED_ORG_KEY)

const orgListeners = new Set<() => void>()

export function setOrgId(orgId: string | null): void {
  if (orgId === currentOrgId) return
  currentOrgId = orgId
  setSelectedOrgId(orgId)
  for (const listener of orgListeners) listener()
}

export const getOrgId = () => currentOrgId

export function subscribeOrg(listener: () => void): () => void {
  orgListeners.add(listener)
  return () => orgListeners.delete(listener)
}

// -- the client --------------------------------------------------------------

/**
 * The app is the first consumer of its own package (ADR-0010): one client,
 * over the shell's fetcher, reading the org at call time so a switch is
 * honoured by the next request rather than the next page load.
 */
export const prodoc = createProDocClient({
  baseUrl: SERVICE_API,
  fetcher: session.authed,
  orgId: getOrgId,
})

/** Settings › Parsing's two routes (engine W12, C-E3) — the app's own, over the same fetcher and org. */
export const parsingApi = createParsingApi({
  baseUrl: SERVICE_API,
  fetcher: session.authed,
  orgId: getOrgId,
})

// -- the fetcher -------------------------------------------------------------

/**
 * **`X-Fiveox-Org`** — the header the whole estate speaks. The prototype's
 * `orgAuthed` already spelt it; this is the template app's `api()` re-based on it.
 */
const ORG_HEADER = 'X-Fiveox-Org'

export interface Envelope<T> {
  success: boolean
  data: T
  meta?: Record<string, unknown>
}

interface Options extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** For routes that are not org-scoped. */
  withoutOrg?: boolean
  /** A step-up assertion from core-mfa, for an action that cannot be undone. */
  stepUpAssertion?: string
}

export async function api<T>(path: string, options: Options = {}): Promise<Envelope<T>> {
  // **A plain object, not a `Headers` instance — and this is not a style
  // choice.** `@fiveox/shell`'s `authed` merges with
  // `{ ...(init.headers ?? {}), authorization: ... }`, and spreading a
  // `Headers` yields `{}` because it has no enumerable own properties. Passing
  // one silently drops every header except the bearer the shell adds itself.
  //
  // That cost the template app an afternoon: `X-Fiveox-Org` never left the browser, the
  // service refused with 400, the UI read it as `authority_unreachable` and
  // told the user the permission service was down (PLATFORM_INTEGRATION §9.3).
  const headers: Record<string, string> = {}
  for (const [key, value] of new Headers(options.headers).entries()) headers[key] = value
  if (options.body !== undefined) headers['content-type'] = 'application/json'
  if (!options.withoutOrg) {
    if (!currentOrgId) {
      throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)
    }
    headers[ORG_HEADER] = currentOrgId
  }
  if (options.stepUpAssertion) headers['X-StepUp-Assertion'] = options.stepUpAssertion

  // `session.authed` owns the bearer, the 401 refresh-and-replay, and ending
  // the session when the refresh comes back spent.
  const res = await session.authed(`${SERVICE_API}${path}`, {
    ...options,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  })

  const body = (await res.json().catch(() => null)) as
    | (Envelope<T> & {
        error?: { code: string; message: string; details?: Record<string, unknown> }
      })
    | null

  if (!res.ok || !body?.success) {
    throw new ApiError(
      body?.error?.code ?? 'INTERNAL',
      body?.error?.message ?? 'Something went wrong.',
      res.status,
      body?.error?.details ?? {},
    )
  }
  return body
}

/** The sentence a screen shows for a failure, whatever threw it. */
export function errorMessage(e: unknown, fallback = 'Something went wrong.'): string {
  if (e instanceof ApiError) return e.message
  if (e instanceof Error && e.message) return e.message
  return fallback
}
